// Billing: plan catalog, entitlement/usage shapes, and request schemas.
// Shared by the API (enforcement + Dodo checkout) and the web app (pricing +
// billing UI) so limits/prices are defined exactly once.
//
// Money model (see docs/economics-modal-gpu.md):
//  - Free is a 7-day, no-card, app-managed trial (not a Dodo subscription).
//  - Starter / Creator / Pro are Dodo subscription products (monthly or annual).
//  - Prices below are GST-INCLUSIVE display prices — the exact amount the
//    customer pays. INR carries India's 18% GST; USD (outside India) has no GST.
//    Dodo is Merchant of Record and issues the GST invoice. Because Dodo adds
//    18% on top of a product's list price for Indian buyers, configure each Dodo
//    INR product's list price = inr / 1.18 so the charged total equals the `inr`
//    shown here. USD products are listed at `usd` as-is.
//  - Overage is a HARD CAP: when a period's included units are spent, the costly
//    action is blocked with an upgrade prompt (never a surprise bill).
import { z } from 'zod';
/** GST rate Dodo applies for Indian customers (shown as an at-checkout note). */
export const GST_RATE = 0.18;
/** No-card trial length for brand-new accounts. */
export const TRIAL_DAYS = 7;
export const PLANS = {
    free: {
        id: 'free',
        name: 'Free trial',
        tagline: 'No card. 7 days to feel the magic.',
        limits: {
            videoMinutes: 5,
            kmagicRuns: 5,
            depthExports: 2,
            maxResolution: '720p',
            watermark: true,
            priorityRender: false,
        },
        features: [
            '5 minutes of video',
            '720p exports (watermarked)',
            '5 kMagic runs',
            '2 depth / behind-the-subject exports',
            'All caption styles + stock B-roll & music',
        ],
    },
    starter: {
        id: 'starter',
        name: 'Starter',
        tagline: 'Go pro on a budget — captioned exports, no watermark.',
        monthly: { usd: 7, inr: 599 },
        annual: { usd: 77, inr: 6590 }, // 1 month free (×11)
        limits: {
            videoMinutes: 40,
            kmagicRuns: 20,
            depthExports: 3,
            maxResolution: '1080p',
            watermark: false,
            priorityRender: false,
        },
        features: [
            '40 minutes of video / month',
            '1080p exports, no watermark',
            '20 kMagic runs / month',
            '3 depth / behind-the-subject exports / month',
            'All caption styles + stock B-roll & music',
        ],
    },
    creator: {
        id: 'creator',
        name: 'Creator',
        tagline: 'For creators shipping short-form weekly.',
        monthly: { usd: 16, inr: 1349 },
        annual: { usd: 176, inr: 14840 }, // 1 month free (×11)
        limits: {
            videoMinutes: 120,
            kmagicRuns: 60,
            depthExports: 20,
            maxResolution: '1080p',
            watermark: false,
            priorityRender: false,
        },
        features: [
            '120 minutes of video / month',
            '1080p exports, no watermark',
            '60 kMagic runs / month',
            '20 depth / behind exports / month',
            'Everything in Starter',
        ],
    },
    pro: {
        id: 'pro',
        name: 'Pro',
        tagline: 'For heavy output and teams of one.',
        monthly: { usd: 29, inr: 2399 },
        annual: { usd: 319, inr: 26390 }, // 1 month free (×11)
        limits: {
            videoMinutes: 500,
            kmagicRuns: 1000, // fair-use "unlimited"
            depthExports: 500, // fair-use "unlimited"
            maxResolution: '1080p',
            watermark: false,
            priorityRender: true,
        },
        features: [
            '500 minutes of video / month',
            '1080p exports, priority render queue',
            'Unlimited kMagic (fair use)',
            'Unlimited depth / behind exports (fair use)',
            'Everything in Creator',
        ],
    },
};
/** Plans that can actually be purchased (excludes the free trial). */
export const PAID_PLAN_IDS = ['starter', 'creator', 'pro'];
export function getPlan(id) {
    return PLANS[id];
}
export function planPrice(id, interval) {
    return interval === 'annual' ? PLANS[id].annual : PLANS[id].monthly;
}
/** India → INR, everyone else → USD. Country is an ISO-3166 alpha-2 code. */
export function currencyForCountry(country) {
    return (country ?? '').toUpperCase() === 'IN' ? 'INR' : 'USD';
}
// --- Usage kinds (UsageRecord.kind values the metering reads) --------------
export const USAGE_KIND = {
    /** Minutes of video transcribed — the "video minutes" meter. */
    minutes: 'transcription_minutes',
    /** One kMagic run. */
    kmagic: 'kmagic_run',
    /** One depth / behind matte generation. */
    depth: 'depth_export',
};
// --- Request schemas -------------------------------------------------------
export const checkoutSchema = z.object({
    planId: z.enum(['starter', 'creator', 'pro']),
    interval: z.enum(['monthly', 'annual']).default('monthly'),
    /** ISO-3166 alpha-2; drives currency + Dodo GST. Optional (Dodo geo-detects). */
    country: z.string().length(2).optional(),
    /** Optional GSTIN for Indian business (B2B) invoices. */
    gstin: z.string().min(15).max(15).optional(),
});
export const changePlanSchema = z.object({
    planId: z.enum(['starter', 'creator', 'pro']),
    interval: z.enum(['monthly', 'annual']).default('monthly'),
});
//# sourceMappingURL=billing.js.map